import React, { FC, useEffect, useMemo, useState } from "react";
import { Button, Col, Drawer, Row, Skeleton, Table, Tooltip } from "antd";
import {
  AppstoreOutlined,
  RightOutlined,
  TeamOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { threeMReportApis } from "../../../api/ThreeMReport";
import { useWbsName } from "../shared/useWbsCodes";
import {
  ThreeMConsolidationData,
  fmtINR,
  fmtINRCompact,
  fmtQty,
  normalizeRollup,
  num,
} from "../reports/ThreeMConsolidation/threeMShared";

// 3M (Material / Manpower / Machinery) summary for the project view page.
// Cards show the project-level rollup from the 3M consolidation report; each
// "View details" opens a drawer with the relevant table — materials from the
// material-summary endpoint, manpower/machinery as a per-task breakdown
// flattened from the consolidation tree.

type ResourceKind = "material" | "manpower" | "machinery";

const CARD_DEFS: {
  key: ResourceKind;
  title: string;
  icon: React.ReactNode;
  color: string;
  tint: string;
}[] = [
  {
    key: "material",
    title: "Materials",
    icon: <AppstoreOutlined />,
    color: "#2557d6",
    tint: "#e5ebfa",
  },
  {
    key: "manpower",
    title: "Manpower",
    icon: <TeamOutlined />,
    color: "#d46b08",
    tint: "#fff7e6",
  },
  {
    key: "machinery",
    title: "Machinery",
    icon: <ToolOutlined />,
    color: "#531dab",
    tint: "#f9f0ff",
  },
];

interface ResourceTaskRow {
  key: number;
  phaseName: string;
  activityName: string;
  taskName: string;
  lines: number;
  amount: number;
}

interface MaterialSummaryRow {
  materialId: number;
  materialName: string;
  materialCode: string;
  uomName: string;
  totalQuantity: string;
  avgRate: string;
  totalCost: string;
  taskCount: number;
  lineCount: number;
}

const eyebrowStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "#8094ae",
};

const Project3MSummary: FC<{ projectId?: number | string }> = ({
  projectId,
}) => {
  const wbsName = useWbsName();
  const [data, setData] = useState<ThreeMConsolidationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [drawerKind, setDrawerKind] = useState<ResourceKind | null>(null);

  const [materialRows, setMaterialRows] = useState<MaterialSummaryRow[] | null>(
    null,
  );
  const [materialLoading, setMaterialLoading] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await threeMReportApis.get3MConsolidation(projectId);
        if (!cancelled && response?.status) setData(response.data || {});
      } catch (err) {
        console.error("Error loading 3M summary:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Reset caches when the project changes
  useEffect(() => {
    setData(null);
    setMaterialRows(null);
    setDrawerKind(null);
  }, [projectId]);

  const totals = normalizeRollup(data?.summary?.totals);
  const amounts: Record<ResourceKind, { amount: number; lines: number }> = {
    material: { amount: totals.materialAmount, lines: totals.materialLineCount },
    manpower: { amount: totals.manpowerAmount, lines: totals.manpowerLineCount },
    machinery: {
      amount: totals.machineryAmount,
      lines: totals.machineryLineCount,
    },
  };

  const openDrawer = (kind: ResourceKind) => {
    setDrawerKind(kind);
    if (kind === "material" && materialRows === null && projectId) {
      setMaterialLoading(true);
      threeMReportApis
        .get3MMaterialSummary(projectId)
        .then((response) => {
          if (response?.status)
            setMaterialRows(response.data?.materials || []);
        })
        .catch((err) => console.error("Error loading material summary:", err))
        .finally(() => setMaterialLoading(false));
    }
  };

  // Per-task rows for manpower / machinery, flattened from the tree
  const taskRows = useMemo<ResourceTaskRow[]>(() => {
    if (!drawerKind || drawerKind === "material") return [];
    const rows: ResourceTaskRow[] = [];
    (data?.phases || []).forEach((phase) =>
      (phase.activities || []).forEach((activity) =>
        (activity.tasks || []).forEach((task) => {
          const rollup = normalizeRollup(task.rollup);
          const amount =
            drawerKind === "manpower"
              ? rollup.manpowerAmount
              : rollup.machineryAmount;
          const lines =
            drawerKind === "manpower"
              ? rollup.manpowerLineCount
              : rollup.machineryLineCount;
          if (amount > 0 || lines > 0) {
            rows.push({
              key: task.taskId,
              phaseName: phase.phaseName,
              activityName: activity.activityName,
              taskName: task.taskName,
              lines,
              amount,
            });
          }
        }),
      ),
    );
    return rows.sort((a, b) => b.amount - a.amount);
  }, [data, drawerKind]);

  const activeDef = CARD_DEFS.find((d) => d.key === drawerKind);

  const materialColumns = [
    {
      title: "Material",
      dataIndex: "materialName",
      key: "materialName",
      render: (name: string, row: MaterialSummaryRow) => (
        <>
          <div style={{ color: "#022c57", fontWeight: 500 }}>{name}</div>
          {row.materialCode ? (
            <div style={{ fontSize: 12, color: "#8094ae" }}>
              {row.materialCode}
            </div>
          ) : null}
        </>
      ),
    },
    {
      title: "Qty",
      dataIndex: "totalQuantity",
      key: "totalQuantity",
      align: "right" as const,
      render: (v: string) => fmtQty(v),
    },
    { title: "UOM", dataIndex: "uomName", key: "uomName", width: 80 },
    {
      title: "Avg Rate",
      dataIndex: "avgRate",
      key: "avgRate",
      align: "right" as const,
      render: (v: string) => fmtINR(v),
    },
    {
      title: "Total Cost",
      dataIndex: "totalCost",
      key: "totalCost",
      align: "right" as const,
      render: (v: string) => fmtINR(v),
    },
  ];

  const taskColumns = [
    {
      title: "Task",
      dataIndex: "taskName",
      key: "taskName",
      render: (name: string, row: ResourceTaskRow) => (
        <>
          <div style={{ color: "#022c57", fontWeight: 500 }}>{name}</div>
          <div style={{ fontSize: 12, color: "#8094ae" }}>
            {wbsName(row.phaseName)} › {wbsName(row.activityName)}
          </div>
        </>
      ),
    },
    {
      title: "Lines",
      dataIndex: "lines",
      key: "lines",
      align: "right" as const,
      width: 80,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      width: 140,
      render: (v: number) => fmtINR(v),
    },
  ];

  const drawerTotal =
    drawerKind === "material"
      ? (materialRows || []).reduce((s, r) => s + num(r.totalCost), 0)
      : taskRows.reduce((s, r) => s + r.amount, 0);

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {CARD_DEFS.map((def) => {
          const { amount, lines } = amounts[def.key];
          return (
            <Col xs={24} md={8} key={def.key}>
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e4e9f0",
                  borderRadius: 12,
                  padding: "14px 16px",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  boxShadow:
                    "0 1px 3px rgba(2, 44, 87, 0.06), 0 3px 10px rgba(2, 44, 87, 0.05)",
                }}
              >
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: def.tint,
                    color: def.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 17,
                    flexShrink: 0,
                  }}
                >
                  {def.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={eyebrowStyle}>{def.title}</div>
                  {loading && !data ? (
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: 90, height: 22, marginTop: 2 }}
                    />
                  ) : (
                    <>
                      <Tooltip title={fmtINR(amount)}>
                        <div
                          style={{
                            fontFamily: "Inter, Nunito, sans-serif",
                            fontSize: 18,
                            fontWeight: 700,
                            color: "#022c57",
                            fontVariantNumeric: "tabular-nums",
                            lineHeight: 1.3,
                          }}
                        >
                          {fmtINRCompact(amount)}
                        </div>
                      </Tooltip>
                      <div style={{ fontSize: 12, color: "#8094ae" }}>
                        {lines} line{lines === 1 ? "" : "s"}
                      </div>
                    </>
                  )}
                </div>
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, flexShrink: 0, fontSize: 13 }}
                  onClick={() => openDrawer(def.key)}
                >
                  View details <RightOutlined style={{ fontSize: 10 }} />
                </Button>
              </div>
            </Col>
          );
        })}
      </Row>

      <Drawer
        title={activeDef ? `${activeDef.title} — Summary` : ""}
        width={720}
        open={!!drawerKind}
        onClose={() => setDrawerKind(null)}
        destroyOnClose
      >
        {drawerKind === "material" ? (
          <Table<MaterialSummaryRow>
            rowKey="materialId"
            size="small"
            loading={materialLoading}
            columns={materialColumns}
            dataSource={materialRows || []}
            pagination={false}
            locale={{ emptyText: "No material lines in this project" }}
          />
        ) : (
          <Table<ResourceTaskRow>
            rowKey="key"
            size="small"
            loading={loading}
            columns={taskColumns}
            dataSource={taskRows}
            pagination={false}
            locale={{
              emptyText: `No ${activeDef?.title.toLowerCase()} lines in this project`,
            }}
          />
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 12,
            paddingTop: 12,
            borderTop: "1px solid #e4e9f0",
            fontWeight: 600,
            color: "#022c57",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          <span>Total</span>
          <span>{fmtINR(drawerTotal)}</span>
        </div>
      </Drawer>
    </>
  );
};

export default Project3MSummary;
